const FOCUSABLE_SELECTOR = [
  'summary',
  'a[href]',
  'button:not([disabled])',
  '[tabindex]:not([tabindex^="-"])',
  '[draggable]',
  'area[href]',
  'input:not([type=hidden]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'object',
  'iframe',
].join(',');

let trapController = null;
let trappedContainer = null;
let firstTrappedElement = null;
let lastTrappedElement = null;
let trapDirection = 1;
let trapRepairFrame = null;

function isHidden(element) {
  if (!(element instanceof HTMLElement)) return true;
  if (element.hidden) return true;
  if (element.closest('[hidden], [inert]')) return true;

  const closedDetails = element.closest('details:not([open])');
  if (closedDetails instanceof HTMLDetailsElement) {
    const summary = closedDetails.querySelector('summary');
    if (!(summary instanceof HTMLElement) || !summary.contains(element)) return true;
  }

  const style = getComputedStyle(element);
  return style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) <= 0;
}

function isDisabled(element) {
  if (element.hasAttribute('disabled')) return true;
  if (element.getAttribute('aria-disabled') === 'true') return true;
  return Boolean(element.closest('fieldset[disabled]'));
}

function isFocusable(element) {
  if (!(element instanceof HTMLElement)) return false;
  if (isHidden(element)) return false;
  if (isDisabled(element)) return false;
  if (element.hasAttribute('tabindex') && element.tabIndex < 0) return false;
  return true;
}

export function findFocusableElements(container) {
  if (!(container instanceof HTMLElement)) return [];
  return [...container.querySelectorAll(FOCUSABLE_SELECTOR)].filter(isFocusable);
}

function refreshTrapBounds() {
  const focusableElements = findFocusableElements(trappedContainer);
  firstTrappedElement = focusableElements[0] || null;
  lastTrappedElement = focusableElements[focusableElements.length - 1] || null;
  return focusableElements;
}

function cancelTrapRepair() {
  if (trapRepairFrame == null) return;
  cancelAnimationFrame(trapRepairFrame);
  trapRepairFrame = null;
}

function scheduleTrapRepair() {
  cancelTrapRepair();

  // Some browsers briefly move focus to <body> before the next tabbable target.
  // Repair that escape after native tab handling if focus left the trapped surface.
  trapRepairFrame = requestAnimationFrame(() => {
    trapRepairFrame = null;
    if (!trappedContainer) return;
    refreshTrapBounds();
    if (!firstTrappedElement || !lastTrappedElement) return;
    if (!document.hasFocus()) return;

    const activeElement = document.activeElement;
    if (activeElement instanceof Node && trappedContainer.contains(activeElement)) return;

    if (trapDirection < 0) {
      lastTrappedElement.focus();
      return;
    }

    firstTrappedElement.focus();
  });
}

function handleTrapKeydown(event) {
  if (event.key !== 'Tab') return;
  if (!trappedContainer) return;

  refreshTrapBounds();
  if (!firstTrappedElement || !lastTrappedElement) return;

  trapDirection = event.shiftKey ? -1 : 1;
  const activeElement =
    event.target instanceof HTMLElement && trappedContainer.contains(event.target) ? event.target : document.activeElement;
  if (!event.shiftKey && activeElement === lastTrappedElement) {
    event.preventDefault();
    firstTrappedElement.focus();
    return;
  }

  if (event.shiftKey && (activeElement === firstTrappedElement || activeElement === trappedContainer)) {
    event.preventDefault();
    lastTrappedElement.focus();
    return;
  }

  scheduleTrapRepair();
}

function handleTrapFocusIn(event) {
  if (!trappedContainer) return;
  refreshTrapBounds();
  if (!firstTrappedElement || !lastTrappedElement) return;
  if (!(event.target instanceof Node)) return;
  if (trappedContainer.contains(event.target)) {
    cancelTrapRepair();
    return;
  }

  event.stopPropagation();
  if (trapDirection < 0) {
    lastTrappedElement.focus();
    return;
  }

  firstTrappedElement.focus();
}

export function trapFocus(container, options = {}) {
  if (!(container instanceof HTMLElement)) return;
  removeTrapFocus();

  trappedContainer = container;
  const focusableElements = refreshTrapBounds();
  if (!focusableElements.length) {
    trappedContainer = null;
    return;
  }
  trapDirection = 1;

  trapController = new AbortController();
  const listenerOptions = {
    capture: true,
    signal: trapController.signal,
    passive: false,
  };

  document.addEventListener('keydown', handleTrapKeydown, listenerOptions);
  document.addEventListener('focusin', handleTrapFocusIn, listenerOptions);

  requestAnimationFrame(() => {
    const initialFocus =
      options?.initialFocus instanceof HTMLElement && container.contains(options.initialFocus)
        ? options.initialFocus
        : firstTrappedElement || trappedContainer;
    initialFocus?.focus();
  });
}

export function removeTrapFocus(container = trappedContainer) {
  if (
    container instanceof HTMLElement &&
    trappedContainer instanceof HTMLElement &&
    container !== trappedContainer
  ) {
    return;
  }

  cancelTrapRepair();
  trapController?.abort();
  trapController = null;
  trappedContainer = null;
  firstTrappedElement = null;
  lastTrappedElement = null;
  trapDirection = 1;
}

export function cycleFocus(items, step) {
  if (!Array.isArray(items) || !items.length) return;
  if (!Number.isFinite(step) || step === 0) return;

  const candidates = items.filter(isFocusable);
  if (!candidates.length) return;

  const activeIndex = candidates.findIndex((element) => element.matches(':focus'));
  const offsetIndex = (activeIndex < 0 ? 0 : activeIndex) + step;
  const normalizedIndex = ((offsetIndex % candidates.length) + candidates.length) % candidates.length;

  candidates[normalizedIndex]?.focus();
}
